import {
  PdfGen,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfBrush,
  PdfFont,
  PdfVector,
  ETextAlign,
  PdfUnit,
} from '../../pdf/pdf';
import { ILayoutListGen } from '../layoutList';
import { IItemCount } from '@educraft/base';
import { OptsList, getListItems } from '../optsList';
import { GUtil } from '../../pdf/gutil';
import { BezierCurve } from '../../pdf/draw/bezier';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/bezier';
import '../../pdf/draw/transforms';

abstract class WordChainBase implements ILayoutListGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 2;
  }
  countMax(): number {
    return 50;
  }

  protected abstract getText(opts: OptsList): string;

  protected getCurve(rnd: { next(a: number, b: number): number }): BezierCurve {
    const margin = PdfUnit.FromMillimeters(15);
    const width = PdfSize.A4.width - 2 * margin;
    const height = PdfSize.A4.height - 2 * margin;

    const variant = rnd.next(0, 3);
    let curve: BezierCurve;

    switch (variant) {
      case 0:
        curve = new BezierCurve(new PdfPoint(margin, margin));
        curve.addSegment(
          new PdfVector(width * 0.5, height * 0.2),
          new PdfPoint(margin + width, height * 0.25),
          new PdfVector(width * -0.0, height * -0.2)
        );
        curve.addSymmetrical(
          new PdfPoint(margin, margin + height * 0.25),
          new PdfVector(0, height * -0.2)
        );
        curve.addSymmetrical(
          new PdfPoint(margin + width * 0.8, margin + height * 0.45),
          new PdfVector(0, height * -0.2)
        );
        curve.addSymmetrical(
          new PdfPoint(margin + width * 0.1, margin + height * 0.6),
          new PdfVector(0, height * -0.2)
        );
        curve.addSymmetrical(
          new PdfPoint(margin + width * 0.95, margin + height * 0.8),
          new PdfVector(0, height * -0.2)
        );
        curve.addSymmetrical(
          new PdfPoint(margin + width * 0.0, margin + height * 1.0),
          new PdfVector(width * 0.2, height * 0.0)
        );
        break;

      case 1: {
        const minus = PdfUnit.FromMillimeters(30);
        curve = new BezierCurve(new PdfPoint(margin, margin));
        curve.addLine(new PdfPoint(margin + width, margin));
        for (let i = 0; i < 3; i++) {
          const offset = i * minus;
          curve.addLine(new PdfPoint(margin + width - offset, margin + height - offset));
          curve.addLine(new PdfPoint(margin + offset, margin + height - offset));
          curve.addLine(new PdfPoint(margin + offset, margin + offset + minus));
          curve.addLine(new PdfPoint(margin + width - offset - minus, margin + offset + minus));
        }
        break;
      }
      case 2:
      default: {
        const minus = PdfUnit.FromMillimeters(40);
        curve = new BezierCurve(new PdfPoint(margin + width / 2, margin));
        curve.addSegment(
          new PdfVector(width * 0.55, 0),
          new PdfPoint(margin + width, margin + height / 2),
          new PdfVector(0, (-height / 2) * 0.55)
        );

        let offset = 0;
        curve.addSymmetrical(
          new PdfPoint(margin + width / 2, margin + height - offset),
          new PdfVector(((width - offset) / 2) * 0.55, 0)
        );
        curve.addSymmetrical(
          new PdfPoint(margin + offset, margin + height / 2),
          new PdfVector(0, ((height - offset) / 2) * 0.55)
        );
        curve.addSymmetrical(
          new PdfPoint(margin + width / 2, margin + offset + minus),
          new PdfVector((-(height - 2 * offset - minus) / 2) * 0.55, 0)
        );
        curve.addSymmetrical(
          new PdfPoint(margin + width - offset - minus, margin + height / 2),
          new PdfVector(0, (-(height - 2 * offset - minus) / 2) * 0.55)
        );
        offset = minus;
        curve.addSymmetrical(
          new PdfPoint(margin + width / 2, margin + height - offset),
          new PdfVector(((width - offset) / 2) * 0.55, 0)
        );
        curve.addSymmetrical(
          new PdfPoint(margin + offset, margin + height / 2),
          new PdfVector(0, ((height - offset) / 2) * 0.55)
        );
        curve.addSymmetrical(
          new PdfPoint(margin + width / 2, margin + offset + minus),
          new PdfVector((-(height - 2 * offset - minus) / 2) * 0.55, 0)
        );
        curve.addSymmetrical(
          new PdfPoint(margin + width / 2, margin + height - offset - minus),
          new PdfVector(0, 0)
        );
        break;
      }
    }

    return curve;
  }

  public async generate(pdf: PdfGen, opts: OptsList): Promise<void> {
    const page = pdf.addPage();
    const curve = this.getCurve(opts.data.random);
    const text = this.getText(opts);

    if (text.length === 0) return;

    const count = 100 * text.length;
    const rawPoints: PdfPoint[] = [];

    for (const segment of curve.segments) {
      for (let i = 0; i <= count; i++) {
        rawPoints.push(segment.calculatePoint(i / count));
      }
    }

    // Filter points by distance
    const filtered: PdfPoint[] = [];
    let last: PdfPoint | null = null;
    const minDistance = PdfUnit.FromMillimeters(1);
    for (const pt of rawPoints) {
      if (
        !last ||
        Math.sqrt((pt.x - last.x)**2 + (pt.y - last.y)**2) >= minDistance
      ) {
        filtered.push(pt);
        last = pt;
      }
    }

    if (filtered.length < 2) return;

    const letters: PdfPoint[] = [];
    for (let i = 0; i <= text.length; i++) {
      // Follow C# logic exactly: i / (text.length + 1)
      const index = Math.floor((i / (text.length + 1)) * (filtered.length - 1));
      letters.push(filtered[index]);
    }

    const font = GUtil.baseFont;

    for (let i = 0; i < text.length; i++) {
      const str = text.substring(i, i + 1);
      const p1 = letters[i];
      const p2 = letters[i + 1];

      if (!p1 || !p2) continue;

      const pMid = new PdfPoint((p1.x + p2.x) / 2, (p1.y + p2.y) / 2);
      // In pdf-lib, positive rotation is counter-clockwise, but Y increases downwards visually.
      // So we need to negate the angle to rotate clockwise.
      let angle = -Math.atan2(p2.y - p1.y, p2.x - p1.x);
      if (isNaN(angle)) angle = 0;

      page.save();
      page.rotateAtTransform((angle * 180) / Math.PI, pMid);

      // Draw centered on point like C# DrawString(str, font, brush, point, formats)
      // We create a small centered rect for drawString to center within.
      const fontSize = font.size;
      const textRect = new PdfRect(
        pMid.x - fontSize,
        pMid.y - fontSize,
        fontSize * 2,
        fontSize * 2
      );
      page.drawString(str, font, PdfBrush.Black, null, textRect, ETextAlign.Center);
      page.restore();
    }
  }
}

/**
 * Slova_na_retezu__Zaklad — word chain generator (basic variant)
 */
export class SlovaNaRetezu__Zaklad extends WordChainBase {
  protected getText(opts: OptsList): string {
    const items = getListItems(opts);
    return items
      .map((i) => i.value)
      .join('')
      .replace(/\s+/g, '');
  }
}

/**
 * Slova_na_retezu__Vypln — word chain with random filler letters
 */
export class SlovaNaRetezu__Vypln extends WordChainBase {
  protected getText(opts: OptsList): string {
    const items = getListItems(opts);
    const available = items
      .map((i) => i.value)
      .join('')
      .replace(/\s+/g, '');
    const rnd = opts.data.random;

    let result = '';
    const addRandom = () => {
      const count = rnd.next(2, 10);
      for (let i = 0; i < count; i++) {
        result += available[rnd.next(0, available.length)];
      }
    };

    addRandom();
    for (const item of items) {
      result += item.value;
      addRandom();
    }
    return result;
  }
}
